import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles, Zap, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { useCredits } from "@/hooks/useCredits";

export const UpgradeBanner = () => {
  const { credits, loading } = useCredits();
  
  // Don't render anything while loading to prevent tier flashing
  if (loading) {
    return null;
  }
  
  const planType = credits?.plan || 'free';

  // Don't show banner for premium users
  if (planType === 'premium') {
    return null;
  }

  // Free tier upgrade banner
  if (planType === 'free') {
    return (
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 overflow-hidden relative">
        <div className="absolute inset-0 bg-[var(--gradient-glow)] opacity-20" />
        <div className="relative p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Free Plan
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {credits?.remaining || 0}/{credits?.quota || 10} credits
                </span>
              </div>
              <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Unlock Pro Features & 100 Credits
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Get ATS Score Checker, LinkedIn Optimizer, Job Analyzer, Salary Tools + 100 credits/month for only $29
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="text-xs">
                  <Zap className="h-3 w-3 mr-1" />
                  100 credits/month
                </Badge>
                <Badge variant="outline" className="text-xs">
                  Up to 50 credit rollover
                </Badge>
                <Badge variant="outline" className="text-xs">
                  5+ Pro tools
                </Badge>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/pricing">
                <Button size="lg" className="gap-2 shadow-lg">
                  Upgrade to Pro
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="sm" variant="ghost" className="text-xs">
                  View all plans
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Pro tier upgrade banner (to Premium)
  if (planType === 'pro') {
    return (
      <Card className="border-2 border-accent/20 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/5 overflow-hidden relative">
        <div className="absolute inset-0 bg-[var(--gradient-glow)] opacity-20" />
        <div className="relative p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-accent/10 text-accent border-accent/20">
                  <Zap className="h-3 w-3 mr-1" />
                  Pro Plan
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {credits?.remaining || 0}/{credits?.quota || 100} credits
                </span>
              </div>
              <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                Upgrade to Premium for AI-Powered Automation
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Get Smart Apply, Company Insights, AI Interview Prep + 200 credits/month + unlimited 1-2 credit actions
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="text-xs">
                  <Crown className="h-3 w-3 mr-1" />
                  200 credits/month
                </Badge>
                <Badge variant="outline" className="text-xs">
                  ✨ Unlimited basic actions
                </Badge>
                <Badge variant="outline" className="text-xs">
                  Up to 100 credit rollover
                </Badge>
                <Badge variant="outline" className="text-xs">
                  Premium AI tools
                </Badge>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/pricing">
                <Button size="lg" variant="default" className="gap-2 shadow-lg bg-gradient-to-r from-accent to-primary">
                  Upgrade to Premium
                  <Crown className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="sm" variant="ghost" className="text-xs">
                  Compare plans
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return null;
};
